import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppProvider } from "@/context/AppContext";
import { UIProvider, useUI } from "@/context/UIContext";
import { Layout } from "@/components/Layout";
import { Login } from "@/pages/Login";
import { AuthCallback } from "@/pages/AuthCallback";
import { CalendarPage } from "@/pages/CalendarPage";
import { UpcomingPage } from "@/pages/Upcoming";
import { TasksPage } from "@/pages/Tasks";
import { CategoriesPage } from "@/pages/Categories";
import { ThemesPage } from "@/pages/Themes";
import { SettingsPage } from "@/pages/Settings";
import { CalendarDays } from "lucide-react";
import { motion } from "framer-motion";

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-app)" }}>
      <motion.div animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 1.4, repeat: Infinity }}
        className="w-14 h-14 rounded-2xl flex items-center justify-center accent-gradient-bg"><CalendarDays size={26} className="text-white" /></motion.div>
    </div>
  );
}

function KeyboardShortcuts() {
  const { setSearchOpen, openEventModal } = useUI();
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen(true); }
      else if (e.key.toLowerCase() === "n" && !["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) { e.preventDefault(); openEventModal({}); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [setSearchOpen, openEventModal]);
  return null;
}

function ProtectedApp() {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <AppProvider>
      <UIProvider>
        <KeyboardShortcuts />
        <Layout />
      </UIProvider>
    </AppProvider>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<LoginGate />} />
      <Route path="/" element={<ProtectedApp />}>
        <Route index element={<Navigate to="/calendar" replace />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="upcoming" element={<UpcomingPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="categories" element={<CategoriesPage />} />
        <Route path="themes" element={<ThemesPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/calendar" replace />} />
    </Routes>
  );
}

function LoginGate() {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (user) return <Navigate to="/calendar" replace />;
  return <Login />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="bottom-right" theme="dark" toastOptions={{ style: { background: "var(--bg-card)", color: "var(--text-primary)", border: "1px solid var(--border-subtle)" } }} />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
